import React, { useEffect, useState } from 'react';
import * as identity from '../identity/identityContract';
import './AccountSettings.css';

/**
 * Account / Security — self-service surface for the CURRENT user.
 * Every operation goes through the engine-neutral identity contract; this page
 * NEVER imports the Supabase client (per the 2026-06-06 identity decision).
 */
export default function AccountSettings() {
  const [me, setMe] = useState(null);
  const [hasPwd, setHasPwd] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  // password form
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [pwBusy, setPwBusy] = useState(false);
  const [pwError, setPwError] = useState(null);
  const [pwSuccess, setPwSuccess] = useState(null);

  // forgot-password
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMsg, setResetMsg] = useState(null);

  useEffect(() => {
    let active = true;
    (async () => {
      const who = await identity.whoAmI();
      if (!active) return;
      if (who.ok) setMe(who.data);
      else setLoadError(who.error.message);

      const hp = await identity.hasPassword();
      if (!active) return;
      if (hp.ok) setHasPwd(hp.data.hasPassword);
      // a has-password read failure is non-fatal; default to change-mode is unsafe,
      // so leave hasPwd null and surface a gentle note in the password section.

      setLoading(false);
    })();
    return () => { active = false; };
  }, []);

  const resetForm = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPwError(null);
    setPwSuccess(null);

    if (newPassword.length < 12) {
      setPwError('Password must be at least 12 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setPwError('Passwords do not match.');
      return;
    }

    setPwBusy(true);
    try {
      const result = hasPwd
        ? await identity.changePassword(currentPassword, newPassword)
        : await identity.setInitialPassword(newPassword);

      if (result.ok) {
        setPwSuccess(hasPwd ? 'Password changed.' : 'Password set.');
        setHasPwd(true);
        resetForm();
      } else {
        setPwError(result.error.message);
      }
    } finally {
      setPwBusy(false);
    }
  };

  const handleForgotPassword = async () => {
    if (!me?.email) return;
    setResetBusy(true);
    setResetMsg(null);
    const result = await identity.requestPasswordReset(me.email);
    setResetBusy(false);
    setResetMsg(
      result.ok
        ? 'If an account exists for this email, a reset link is on its way.'
        : `Could not send reset link: ${result.error.message}`,
    );
  };

  if (loading) {
    return <div className="acct-page"><div className="acct-loading">Loading account…</div></div>;
  }

  return (
    <div className="acct-page">
      <div className="acct-header">
        <h2>Account</h2>
        <p className="acct-subtitle">Manage your profile and password.</p>
      </div>

      {loadError && <div className="acct-banner acct-banner-error">{loadError}</div>}

      {/* Profile */}
      <section className="acct-card">
        <h3>Profile</h3>
        <div className="acct-field-row">
          <span className="acct-label">Email</span>
          <span className="acct-value">{me?.email || '—'}</span>
        </div>
        <div className="acct-field-row">
          <span className="acct-label">Role</span>
          <span className="acct-value">{me?.role || '—'}</span>
        </div>
      </section>

      {/* Password */}
      <section className="acct-card">
        <h3>{hasPwd ? 'Change password' : 'Set a password'}</h3>
        {hasPwd === null && (
          <p className="acct-note">Could not determine password status; you can still set a new password below.</p>
        )}
        <form onSubmit={handlePasswordSubmit} className="acct-form">
          {hasPwd && (
            <label className="acct-input-label">
              Current password
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                autoComplete="current-password"
                required
              />
            </label>
          )}
          <label className="acct-input-label">
            New password
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>
          <label className="acct-input-label">
            Confirm new password
            <input
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              required
            />
          </label>

          {pwError && <div className="acct-banner acct-banner-error">{pwError}</div>}
          {pwSuccess && <div className="acct-banner acct-banner-success">{pwSuccess}</div>}

          <div className="acct-actions">
            <button type="submit" className="acct-btn-primary" disabled={pwBusy}>
              {pwBusy ? 'Saving…' : (hasPwd ? 'Change password' : 'Set password')}
            </button>
            {hasPwd && (
              <button
                type="button"
                className="acct-btn-link"
                onClick={handleForgotPassword}
                disabled={resetBusy}
              >
                {resetBusy ? 'Sending…' : 'Forgot password?'}
              </button>
            )}
          </div>
          {resetMsg && <div className="acct-banner acct-banner-info">{resetMsg}</div>}
        </form>
      </section>
    </div>
  );
}
